import React, { useEffect, useState } from 'react';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import { StoreSearch } from '@thd-olt-component-react/store-search';
import { ProductOverviewAccordionBody } from '@thd-olt-component-react/product-overview';
import { SpecificationsAccordionBody } from '@thd-olt-component-react/specifications';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import { Link } from '@one-thd/sui-atomic-components';

import { useStore } from '@thd-nucleus/experience-context';
import { useUsedLocationSearch } from '../hooks/useUsedLocationSearch';
import { useUsedProductInventory } from '../hooks/useUsedProductInventory';
import { useUsedProductFilter } from '../hooks/useUsedProductFilter';
import { usedProductDetailDataModel } from '../hooks/useUsedProductDetail';
import { UsedProductCard } from '../components/used-equipment/UsedProductCard';
import { UsedLocationSearch } from '../components/used-equipment/UsedLocationSearch';
import { UsedFilterSidebar } from '../components/used-equipment/UsedFilterSidebar';
import { UsedProductGrid } from '../components/used-equipment/UsedProductGrid';
import { UsedEquipmentQuickView } from '../components/used-equipment/UsedEquipmentQuickView';

import '../styles/pages/UsedEquipment.scss';

const USED_EQUIPMENT_DATA_MODEL = extend(
  StoreSearch,
  UsedProductCard,
  usedProductDetailDataModel,
  ProductOverviewAccordionBody,
  SpecificationsAccordionBody
);

function UsedEquipmentContent() {
  const localizedStore = useStore({ online: true });

  const { searchInput, setSearchInput, searchCoordinates, setSearchCoordinates } = useUsedLocationSearch();

  const { inventory, loading: inventoryLoading, error: inventoryError, fetchInventory } = useUsedProductInventory();

  const [selectedStore, setSelectedStore] = useState(null);
  const [nearbyStores, setNearbyStores] = useState([]);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [quickViewItem, setQuickViewItem] = useState(null);

  const {
    filteredItems,
    keyword,
    categories,
    appliedDimensions,
    sortMethod,
    selectedStoreIds,
    setKeyword,
    toggleCategory,
    toggleStore,
    onDimensionsChange,
    clearAllRefinements,
    clearStoreFilter,
    setSortMethod,
    initCategories,
    sortMethods,
  } = useUsedProductFilter(inventory);

  // Seed search with localized store or default zip on first render
  useEffect(() => {
    if (localizedStore?.storeId && localizedStore.storeId !== '8119') {
      setSearchInput(localizedStore.storeId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localizedStore?.storeId]);

  // Rebuild category filters whenever inventory changes
  useEffect(() => {
    initCategories(inventory);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory]);

  // Called by UsedLocationSearch whenever a search returns stores — loads all nearby inventory
  const handleStoresLoaded = (stores) => {
    if (!stores?.length) return;
    setSelectedStore(stores[0]);
    setNearbyStores(stores);
    clearStoreFilter();
    const storeMap = new Map(stores.map((store) => [store.storeId, store]));
    fetchInventory(stores.map((store) => store.storeId), storeMap);
  };

  const openQuickView = (item) => {
    setQuickViewItem(item);
    setIsQuickViewOpen(true);
  };

  const closeQuickView = () => {
    setIsQuickViewOpen(false);
    setQuickViewItem(null);
  };

  return (
    <Row className="isBound">
      <Col>
        <div className="used-equipment-page">
          <div className="used-equipment-header">
            <div className="used-equipment-header-inner">
              <nav className="used-equipment-breadcrumbs" aria-label="Breadcrumb">
                <ol
                  className="used-equipment-breadcrumb-list"
                >
                  <li>
                    <Link href="/" color="subtle">
                      Home
                    </Link>
                  </li>
                  <li className="used-equipment-breadcrumb-separator" aria-hidden="true">/</li>
                  <li>
                    <Link
                      href="/c/tool-and-equipment-rental"
                      color="subtle"
                    >
                      Tool &amp; Equipment Rental
                    </Link>
                  </li>
                  <li className="used-equipment-breadcrumb-separator" aria-hidden="true">/</li>
                  <li aria-current="page" className="used-equipment-breadcrumb-current">Used Tools for Sale</li>
                </ol>
              </nav>

              <div className="used-equipment-copy-block">
                <h1 className="used-equipment-title">USED EQUIPMENT</h1>
                <div className="used-equipment-description-block">
                  <p className="used-equipment-description">
                    At The Home Depot, we strive to offer the highest quality products at competitive prices.
                    In order to do so, we leverage the professional-grade tools that cycle through our rental
                    program and sell them while still in good, working condition. Though slightly used, these
                    tools have been serviced between each rental to assure performance quality. Below, you can
                    search by zip code, store, keywords, and product categories.
                  </p>

                  <ul className="used-equipment-bullet-list">
                    <li>Brand/model may vary based on availability and location</li>
                    <li>
                      Previously rented tools and equipment sold &quot;as-is&quot; (See{' '}
                      <Link href="/c/Tool_Rental_FAQ" target="_blank">
                        Terms and Conditions
                      </Link>
                      )
                    </li>
                    <li>
                      Used equipment is tested and inspected by The Home Depot&apos;s Tool Rental technicians
                      prior to being placed for sale.
                    </li>
                  </ul>
                </div>
              </div>

            </div>
          </div>

          {/* Body: sidebar + grid */}
          <div className="used-equipment-body sui-p-4">
            <div className="used-equipment-content sui-flex sui-gap-4">
              {/* Sidebar */}
              <div className="used-equipment-sidebar sui-w-56 sui-flex-shrink-0">
                <UsedLocationSearch
                  searchInput={searchInput}
                  setSearchInput={setSearchInput}
                  searchCoordinates={searchCoordinates}
                  setSearchCoordinates={setSearchCoordinates}
                  selectedStore={selectedStore}
                  onStoresLoaded={handleStoresLoaded}
                />
                <UsedFilterSidebar
                  categories={categories}
                  keyword={keyword}
                  onKeywordChange={setKeyword}
                  onToggleCategory={toggleCategory}
                  onDimensionsChange={onDimensionsChange}
                  appliedDimensions={appliedDimensions}
                  onClearAllRefinements={clearAllRefinements}
                  resultCount={filteredItems.length}
                  stores={nearbyStores.map((store) => ({
                    ...store,
                    count: inventory.filter((item) => item.StrNbr === store.storeId).length,
                  }))}
                  selectedStoreIds={selectedStoreIds}
                  onToggleStore={toggleStore}
                />
              </div>

              {/* Main content */}
              <main className="used-equipment-main sui-flex-1 sui-min-w-0">
                {inventoryError && (
                  <p className="sui-text-danger sui-text-sm sui-mb-3" role="alert">
                    {inventoryError}
                  </p>
                )}

                {/* Toolbar */}
                <div className="sui-flex sui-items-center sui-justify-between sui-mb-4">
                  <p
                    className="sui-font-bold sui-text-lg sui-leading-tight
                      sui-tracking-normal sui-normal-case sui-line-clamp-unset
                      sui-font-normal sui-text-primary"
                  >
                    {filteredItems.length} Results
                  </p>
                  <div className="sui-flex sui-items-center sui-gap-2">
                    <span className="sui-text-sm">Sort By</span>
                    <select
                      id="used-sort-select"
                      aria-label="Sort By"
                      value={sortMethod.name}
                      onChange={(event) => {
                        const method = sortMethods.find((sortOption) => sortOption.name === event.target.value);
                        if (method) setSortMethod(method);
                      }}
                      className="sui-text-sm sui-border sui-border-primary sui-rounded sui-px-2 sui-py-1"
                    >
                      {sortMethods.map((sortOption) => (
                        <option key={sortOption.name} value={sortOption.name}>{sortOption.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <UsedProductGrid
                  items={filteredItems}
                  onQuickViewClick={openQuickView}
                  loading={inventoryLoading}
                />
              </main>
            </div>
          </div>

          {/* Quick view drawer */}
          <UsedEquipmentQuickView
            item={quickViewItem}
            isOpen={isQuickViewOpen}
            onClose={closeQuickView}
          />
        </div>
      </Col>
    </Row>
  );
}

UsedEquipmentContent.dataModel = USED_EQUIPMENT_DATA_MODEL;

export const UsedEquipment = () => (
  <QueryProvider
    cacheKey="used-equipment"
    defaultVariables={{ dataSource: 'catalog', fetchPolicy: 'cache-first' }}
  >
    <UsedEquipmentContent />
  </QueryProvider>
);

UsedEquipment.displayName = 'UsedEquipment';
UsedEquipment.dataModel = USED_EQUIPMENT_DATA_MODEL;
